import { useState, type CSSProperties } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useActiveAthlete } from "@/hooks/useActiveAthlete";
import { useAuth } from "@/hooks/useAuth";
import JournalEntry from "@/components/JournalEntry";
import NewJournalEntryForm from "@/components/NewJournalEntryForm";
import JournalingAssistant from "@/components/JournalingAssistant";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, CalendarIcon, Filter } from "lucide-react";

const legacyTheme: CSSProperties = {
  "--background": "220 58% 9%",
  "--foreground": "0 0% 100%",
  "--card": "221 45% 14%",
  "--card-foreground": "0 0% 100%",
  "--muted": "221 35% 18%",
  "--muted-foreground": "215 20% 80%",
  "--border": "40 70% 55%",
  "--input": "221 25% 20%",
  "--ring": "40 70% 55%",
};

const Journal = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [journalFormOpen, setJournalFormOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [editingEntry, setEditingEntry] = useState<any>(null);

  // Get the active athlete (shared across the app; switchable)
  const { athlete } = useActiveAthlete();

  // Get journal entries
  const { data: journalEntries, isLoading } = useQuery({
    queryKey: ["/api/athletes", athlete?.id, "journal"],
    enabled: !!athlete?.id,
  });

  // Get journal prompts
  const { data: journalPrompts } = useQuery({
    queryKey: ["/api/journal-prompts"],
  });

  const handleEditEntry = (entry: any) => {
    setEditingEntry(entry);
    setJournalFormOpen(true);
  };

  // Filter and search journal entries
  const filteredEntries = journalEntries?.filter((entry: any) => {
    // Search filter
    const matchesSearch = 
      searchQuery === "" || 
      entry.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.content.toLowerCase().includes(searchQuery.toLowerCase());
      
    // Category filter
    const matchesCategory = 
      categoryFilter === "all" || 
      (entry.tags && entry.tags.some((tag: string) => 
        tag.toLowerCase() === categoryFilter.toLowerCase()
      ));
      
    return matchesSearch && matchesCategory;
  }) || [];

  // Get unique tags for filter
  const uniqueTags = Array.from(
    new Set(
      journalEntries?.flatMap(entry => entry.tags || []) || []
    )
  );

  return (
    <div className="container mx-auto min-h-screen px-4 py-6 text-white" style={legacyTheme}>
      <div className="mb-6 flex flex-col gap-4 rounded-xl border border-[#E0A458]/45 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] px-4 py-4 shadow-lg shadow-black/25 md:flex-row md:items-center md:justify-between">
        <h1 className="font-montserrat font-bold text-2xl md:text-3xl text-white">Journal</h1>
        <Dialog open={journalFormOpen} onOpenChange={setJournalFormOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#E0A458] text-black hover:bg-[#f0b467] flex items-center font-semibold">
              <Plus className="h-4 w-4 mr-2" /> New Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <NewJournalEntryForm 
              athleteId={athlete?.id}
              onSuccess={() => {
                setJournalFormOpen(false);
                setEditingEntry(null);
                queryClient.invalidateQueries({ queryKey: ["/api/athletes", athlete?.id, "journal"] });
              }}
              entryToEdit={editingEntry}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Journal Entries */}
        <div className="lg:col-span-2">
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-grow">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-neutral-400" />
                  <Input
                    placeholder="Search journal entries..."
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Select
                    value={categoryFilter}
                    onValueChange={setCategoryFilter}
                  >
                    <SelectTrigger className="w-[180px]">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Filter by category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {uniqueTags.map((tag) => (
                        <SelectItem key={tag} value={tag}>
                          {tag}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="all">
            <TabsList className="mb-4">
              <TabsTrigger value="all">All Entries</TabsTrigger>
              <TabsTrigger value="recent">Recent</TabsTrigger>
              <TabsTrigger value="enhanced">AI Enhanced</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="space-y-4">
              {isLoading ? (
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-slate-300">Loading journal entries...</p>
                  </CardContent>
                </Card>
              ) : filteredEntries.length > 0 ? (
                filteredEntries.map((entry) => (
                  <JournalEntry 
                    key={entry.id} 
                    entry={entry} 
                    onEdit={handleEditEntry}
                  />
                ))
              ) : (
                <Card>
                  <CardContent className="p-6 text-center">
                    <p className="text-slate-300">No journal entries found.</p>
                    <Button 
                      variant="link" 
                      onClick={() => setJournalFormOpen(true)}
                      className="mt-2"
                    >
                      Create your first entry
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            <TabsContent value="recent" className="space-y-4">
              {isLoading ? (
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-slate-300">Loading recent entries...</p>
                  </CardContent>
                </Card>
              ) : filteredEntries.slice(0, 5).length > 0 ? (
                filteredEntries.slice(0, 5).map((entry) => (
                  <JournalEntry 
                    key={entry.id} 
                    entry={entry} 
                    onEdit={handleEditEntry}
                  />
                ))
              ) : (
                <Card>
                  <CardContent className="p-6 text-center">
                    <p className="text-slate-300">No recent journal entries found.</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            <TabsContent value="enhanced" className="space-y-4">
              {isLoading ? (
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-slate-300">Loading AI enhanced entries...</p>
                  </CardContent>
                </Card>
              ) : filteredEntries.filter(entry => entry.aiEnhanced).length > 0 ? (
                filteredEntries
                  .filter(entry => entry.aiEnhanced)
                  .map((entry) => (
                    <JournalEntry 
                      key={entry.id} 
                      entry={entry} 
                      onEdit={handleEditEntry}
                    />
                  ))
              ) : (
                <Card>
                  <CardContent className="p-6 text-center">
                    <p className="text-slate-300">No AI enhanced entries found.</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Enable AI enhancement when creating a new entry.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <JournalingAssistant athleteId={athlete?.id} />
          
          <Card>
            <CardHeader className="border-b border-[#E0A458]/30 bg-gradient-to-r from-[#2B4368]/70 via-[#223657]/65 to-[#172844]/70 pb-3">
              <CardTitle className="text-lg flex items-center">
                <CalendarIcon className="h-5 w-5 mr-2 text-primary" />
                Recent Journal Categories
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-2">
                {uniqueTags.map((tag) => (
                  <Badge 
                    key={tag} 
                    variant="outline" 
                    className="px-3 py-1 bg-primary/10 text-primary cursor-pointer hover:bg-primary/20"
                    onClick={() => setCategoryFilter(tag)}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
              
              {uniqueTags.length === 0 && (
                <p className="text-slate-400 text-sm text-center py-2">
                  No categories found. Add tags to your journal entries.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-b border-[#E0A458]/30 bg-gradient-to-r from-[#2B4368]/70 via-[#223657]/65 to-[#172844]/70 pb-3">
              <CardTitle className="text-lg flex items-center">
                <Plus className="h-5 w-5 mr-2 text-primary" />
                Journal Statistics
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-300">Total Entries</span>
                  <span className="font-bold text-primary">{journalEntries?.length || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-300">AI Enhanced</span>
                  <span className="font-bold text-primary">
                    {journalEntries?.filter(entry => entry.aiEnhanced).length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-300">This Month</span>
                  <span className="font-bold text-primary">
                    {journalEntries?.filter(entry => {
                      const entryDate = new Date(entry.date);
                      const now = new Date();
                      return entryDate.getMonth() === now.getMonth() &&
                             entryDate.getFullYear() === now.getFullYear();
                    }).length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-300">Categories</span>
                  <span className="font-bold text-primary">{uniqueTags.length}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Journal;
